import { ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { fetchRetirements } from "@/services/api";
import { Retirement } from "@/types/retirement";
import formatCO2eCredits from "@/utils/formatAmountToCO2Credits";

const RetirementsList: React.FC<{ transactionIds: string[] }> = ({
  transactionIds,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retirements, setRetirements] = useState<Retirement[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { t, language } = useLanguage();

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const retirements = await Promise.all(
        transactionIds.map((id) => fetchRetirements(id)),
      );
      setRetirements(retirements.flat());
    } catch (e: any) {
      setError(e.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!expanded && retirements === null) {
      fetchData();
    }
    setExpanded((v) => !v);
  };

  return (
    <div className="mt-4 w-full">
      <Button
        id="retirements-toggle-button"
        variant="secondary"
        size="sm"
        className="mb-2"
        onClick={handleToggle}
        aria-expanded={expanded}
        aria-loading={loading}
      >
        {t("confirmation.showRetirements") || "Show retirements"}
        {expanded ? (
          <ChevronUp className="ml-2 h-4 w-4" />
        ) : (
          <ChevronDown className="ml-2 h-4 w-4" />
        )}
      </Button>
      {expanded && (
        <div className="mt-2">
          {loading && (
            <div className="text-sm text-muted-foreground">
              {t("common.loading")}
            </div>
          )}
          {error && <div className="text-sm text-red-500">{error}</div>}
          {retirements && retirements.length === 0 && (
            <div className="text-sm text-muted-foreground">
              {t("confirmation.noRetirements") || "No retirements found."}
            </div>
          )}
          {retirements && retirements.length > 0 && (
            <table className="w-full space-y-2">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="text-xs text-muted-foreground px-3 py-2 text-left"
                  >
                    {t("confirmation.retirementDate")}
                  </th>
                  <th
                    scope="col"
                    className="text-xs text-muted-foreground px-3 py-2 text-left"
                  >
                    {t("confirmation.retirementAmount")}
                  </th>
                  <th
                    scope="col"
                    className="text-xs text-muted-foreground px-3 py-2 text-left"
                  >
                    {t("confirmation.retirementDescription")}
                  </th>
                  <th
                    scope="col"
                    className="text-xs text-muted-foreground px-3 py-2 text-left"
                  >
                    {t("confirmation.externalConfirmation")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {retirements.map((r) => (
                  <tr key={r.id} className="border rounded p-2 bg-white">
                    <td className="text-xs text-muted-foreground px-3 py-2">
                      {new Intl.DateTimeFormat(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }).format(new Date(r.createdAt))}
                    </td>
                    <td className="text-xs text-muted-foreground px-3 py-2">
                      {formatCO2eCredits(r.amount, t, language)}
                    </td>
                    <td className="text-xs text-foreground px-3 py-2">
                      {r.description ||
                        t("confirmation.noRetirementDescription")}
                    </td>
                    <td className="text-xs text-foreground px-3 py-2">
                      {r.externalConfirmationURL ? (
                        <a
                          href={r.externalConfirmationURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-800"
                        >
                          {t("confirmation.viewExternal")}
                          <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default RetirementsList;
